// umlaut-check: skip — "begruendung"/"lagerWerte" sind Feld-/State-Namen für
// den Request an /cosmos/daten/einstellungen (technische Bezeichner), bleiben
// ASCII; die sichtbaren Texte tragen "Begründung" korrekt geschrieben.

import React, {useState} from "react";
import axios from "axios";
import {OverlayTrigger, Table, Tooltip} from "react-bootstrap";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import SelectList from "../SelectList.jsx";
import {friendlyErrorMessage} from "../SalesAnalysis/format.js";
import {formatDatumZeit, formatMenge, jaNeinLabel, lagerLabel, produktlinieLabel} from "./format.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

const SICHERHEIT_BADGE = {
    aus_datei: {klasse: "text-bg-secondary", label: "aus Datei"},
    gemessen: {klasse: "text-bg-info", label: "gemessen"},
    angenommen: {klasse: "text-bg-warning", label: "angenommen"},
    gepflegt: {klasse: "text-bg-primary", label: "gepflegt"},
};

// Rückfall, solange eine Einstellung noch kein `label` vom Backend mitbringt
// (Sichtprüfung 1, Befund 18: der rohe Schlüssel "bestellmenge_wochen" stand
// unübersetzt in der Oberfläche).
const LABEL_FALLBACK = {
    bestellmenge_wochen: "Bestellmenge",
    lager_je_produktlinie: "Lager je Produktlinie",
    lahr_freigegeben: "Lager Lahr freigegeben",
    lieferrhythmus_wochen: "Lieferrhythmus",
    lkw_kapazitaet_stueck: "LKW-Kapazität",
    mindestbestand_wochen: "Mindestbestand",
    nachbestellschwelle_snv: "Nachbestellschwelle SNV",
    palette_stueck: "Stück je Palette",
    prognose_wochen: "Prognosehorizont",
    saison_jahre: "Zeitraum für Saisonfaktoren",
    shop_schwelle_nicht_lieferbar: "Schwelle „nicht lieferbar“ im Shop",
    sicherheitspuffer_tage: "Sicherheitspuffer",
    verbrauch_basis_wochen: "Basis für den Wochenbedarf",
    verspaetung_mittel_tage: "Mittlere Verspätung",
    viertelpalette_stueck: "Stück je Viertelpalette",
    wiederbeschaffung_tage: "Wiederbeschaffungszeit",
};

const PRODUKTLINIEN_REIHENFOLGE = ["PREMIUM", "FLAME_ORANGE", "FLAME_BLUE", "BURNER"];

const LAGER_OPTIONEN = [
    {value: 8034, label: "SNV Weilheim (8034)"},
    {value: 3827, label: "Lahr (3827)"},
];

const ZELLE_UMBRUCH = {whiteSpace: "normal", wordBreak: "break-word"};

function prettifySchluessel(schluessel) {
    return String(schluessel)
        .split("_")
        .map((teil) => (teil ? teil.charAt(0).toUpperCase() + teil.slice(1) : teil))
        .join(" ");
}

function einstellungLabel(e) {
    return e.label || LABEL_FALLBACK[e.schluessel] || prettifySchluessel(e.schluessel);
}

/** Rückfall, solange eine Einstellung noch kein `eingabe` vom Backend mitbringt. */
function eingabeTyp(e) {
    if (e.eingabe) {
        return e.eingabe;
    }
    if (e.schluessel === "lager_je_produktlinie") {
        return "lager_zuordnung";
    }
    if (e.schluessel === "lahr_freigegeben") {
        return "ja_nein";
    }
    return "zahl";
}

function parseLagerZuordnung(wertRoh) {
    if (!wertRoh) {
        return {};
    }
    if (typeof wertRoh === "object") {
        return wertRoh;
    }
    try {
        return JSON.parse(wertRoh) || {};
    } catch (fehler) {
        return {};
    }
}

function lagerZuordnungZeilen(wertRoh) {
    const objekt = parseLagerZuordnung(wertRoh);
    return PRODUKTLINIEN_REIHENFOLGE.map((produktlinie) => ({produktlinie, lagerId: objekt[produktlinie] ?? null}));
}

/** Rückfall, solange eine Einstellung noch kein `anzeige_wert` vom Backend mitbringt. */
function anzeigeWert(e) {
    if (e.anzeige_wert !== undefined && e.anzeige_wert !== null && e.anzeige_wert !== "") {
        return e.anzeige_wert;
    }
    const typ = eingabeTyp(e);
    if (typ === "ja_nein") {
        return jaNeinLabel(e.wert);
    }
    if (typ === "lager_zuordnung") {
        return lagerZuordnungZeilen(e.wert)
            .map((z) => `${produktlinieLabel(z.produktlinie)} → ${lagerLabel(z.lagerId)}`)
            .join(" · ");
    }
    if (typ === "text") {
        return e.wert || "–";
    }
    return `${formatMenge(e.wert)}${e.einheit ? ` ${e.einheit}` : ""}`;
}

/**
 * "Annahmen und Stellschrauben" (PLAN.md Abschnitt 4a.4): jede Einstellung
 * aus cosmos_einstellungen mit ihrer Sicherheit, inline bearbeitbar nur mit
 * "cosmos.parameters" - die Begründung ist Pflichtfeld und wird zur neuen
 * Herkunft, die Sicherheit springt danach auf "gepflegt". Nach dem
 * Speichern erscheint der Hinweis "wirkt erst nach der nächsten
 * Berechnung" samt Knopf, den `onGeaendert` an die Seite weiterreicht.
 *
 * Sichtprüfung 1, Befund 18: Einstellung, Wert und Bearbeiten-Feld zeigten
 * bis dahin rohe Schlüssel/JSON/0-1 statt Klartext - `label`, `anzeige_wert`
 * und `eingabe` kommen künftig vom Backend (K3), mit lokalem Rückfall, falls
 * eines der Felder noch fehlt. table-layout: fixed löst außerdem Befund 25
 * (Tabelle passt jetzt ohne waagerechtes Scrollen in 1440 px, das
 * Bearbeiten-Formular bleibt an der Zeile sichtbar).
 */
export default function EinstellungenTabelle({einstellungen, canParameters, onGeaendert}) {
    const {addToast} = useExternalControls();
    const [bearbeiteId, setBearbeiteId] = useState(null);
    const [wert, setWert] = useState("");
    const [lagerWerte, setLagerWerte] = useState({});
    const [begruendung, setBegruendung] = useState("");
    const [speichert, setSpeichert] = useState(false);

    if (!einstellungen || einstellungen.length === 0) {
        return null;
    }

    const start = (e) => {
        setBearbeiteId(e.id);
        const typ = eingabeTyp(e);
        if (typ === "lager_zuordnung") {
            setLagerWerte(parseLagerZuordnung(e.wert));
        } else if (typ === "ja_nein") {
            setWert(Number(e.wert) ? "1" : "0");
        } else {
            setWert(e.wert);
        }
        setBegruendung("");
    };

    const abbrechen = () => {
        setBearbeiteId(null);
        setWert("");
        setLagerWerte({});
        setBegruendung("");
    };

    const speichern = (e) => {
        if (!begruendung.trim()) {
            return;
        }
        const typ = eingabeTyp(e);
        const payloadWert = typ === "lager_zuordnung" ? JSON.stringify(lagerWerte) : wert;
        setSpeichert(true);
        axios
            .put(`/cosmos/daten/einstellungen/${e.schluessel}`, {wert: payloadWert, begruendung})
            .then(() => {
                addToast(<FlexToast variant="success" title="Cosmos" autohide message="Einstellung geändert – wirkt nach der nächsten Berechnung." />);
                abbrechen();
                onGeaendert?.();
            })
            .catch((fehler) => {
                addToast(<FlexToast variant="danger" title="Cosmos" autohide={false} message={friendlyErrorMessage(fehler, "Die Einstellung konnte nicht gespeichert werden.")} />);
            })
            .finally(() => setSpeichert(false));
    };

    return (
        <Table responsive size="sm" className="align-middle" style={{tableLayout: "fixed", width: "100%"}}>
            <thead>
                <tr>
                    <th style={{width: "20%"}}>
                        <HeaderWithHelp help="Name der Annahme oder Stellschraube.">Einstellung</HeaderWithHelp>
                    </th>
                    <th style={{width: "18%"}}>
                        <HeaderWithHelp help="Aktuell gültiger Wert, mit Einheit.">Wert</HeaderWithHelp>
                    </th>
                    <th style={{width: "10%"}}>
                        <HeaderWithHelp help="Woher der Wert stammt: aus einer Datei, gemessen, angenommen oder von Hand gepflegt.">Sicherheit</HeaderWithHelp>
                    </th>
                    <th style={{width: "28%"}}>
                        <HeaderWithHelp help="Genauere Begründung, wie der Wert zustande kam.">Herkunft</HeaderWithHelp>
                    </th>
                    <th style={{width: "14%"}}>
                        <HeaderWithHelp help="Wer den Wert zuletzt geändert hat und wann.">Geändert</HeaderWithHelp>
                    </th>
                    {canParameters && (
                        <th style={{width: "10%"}}>
                            <HeaderWithHelp help="Wert dieser Einstellung ändern.">Aktion</HeaderWithHelp>
                        </th>
                    )}
                </tr>
            </thead>
            <tbody>
                {einstellungen.map((e) => {
                    const badge = SICHERHEIT_BADGE[e.sicherheit] || {klasse: "text-bg-secondary", label: e.sicherheit || "–"};
                    const wirdBearbeitet = bearbeiteId === e.id;
                    const typ = eingabeTyp(e);
                    const label = einstellungLabel(e);

                    return (
                        <React.Fragment key={e.id}>
                            <tr>
                                <td style={ZELLE_UMBRUCH}>
                                    <HeaderWithHelp help={e.beschreibung}>
                                        <span className="fw-semibold">{label}</span>
                                    </HeaderWithHelp>
                                </td>
                                <td style={ZELLE_UMBRUCH}>{anzeigeWert(e)}</td>
                                <td>
                                    <span className={`badge ${badge.klasse}`}>{badge.label}</span>
                                </td>
                                <td className="small text-muted" style={ZELLE_UMBRUCH}>
                                    {e.herkunft}
                                </td>
                                <td className="small text-muted" style={ZELLE_UMBRUCH}>
                                    {e.geaendert_von ? `${e.geaendert_von}, ${formatDatumZeit(e.updated_at)}` : "–"}
                                </td>
                                {canParameters && (
                                    <td>
                                        {!wirdBearbeitet && (
                                            <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-einstellung-bearbeiten-${e.id}`}>Öffnet ein Feld, um „{label}“ mit Begründung zu ändern.</Tooltip>}>
                                                <span>
                                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => start(e)}>
                                                        Bearbeiten
                                                    </button>
                                                </span>
                                            </OverlayTrigger>
                                        )}
                                    </td>
                                )}
                            </tr>
                            {wirdBearbeitet && (
                                <tr>
                                    <td colSpan={canParameters ? 6 : 5} className="bg-body-secondary">
                                        <div className="d-flex flex-wrap align-items-end gap-3">
                                            <div style={{minWidth: 220}}>
                                                <HeaderWithHelp help={typ === "zahl" ? `Neuer Wert${e.einheit ? " in " + e.einheit : ""}.` : "Neuer Wert dieser Einstellung."}>
                                                    <span className="form-label small mb-1 d-block">Neuer Wert</span>
                                                </HeaderWithHelp>

                                                {typ === "ja_nein" && (
                                                    <div className="form-check form-switch">
                                                        <input
                                                            id={`cosmos-einstellung-wert-${e.id}`}
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            checked={wert === "1"}
                                                            onChange={(ev) => setWert(ev.target.checked ? "1" : "0")}
                                                        />
                                                        <label htmlFor={`cosmos-einstellung-wert-${e.id}`} className="form-check-label small">
                                                            {wert === "1" ? "Ja" : "Nein"}
                                                        </label>
                                                    </div>
                                                )}

                                                {typ === "lager_zuordnung" && (
                                                    <div className="d-flex flex-column gap-1">
                                                        {PRODUKTLINIEN_REIHENFOLGE.map((produktlinie) => (
                                                            <div key={produktlinie} className="d-flex align-items-center gap-2">
                                                                <span className="small text-muted" style={{minWidth: 110}}>
                                                                    {produktlinieLabel(produktlinie)}
                                                                </span>
                                                                <SelectList
                                                                    value={lagerWerte[produktlinie] ?? ""}
                                                                    options={LAGER_OPTIONEN}
                                                                    onChange={(v) => setLagerWerte((bisher) => ({...bisher, [produktlinie]: Number(v)}))}
                                                                    size="sm"
                                                                    ariaLabel={`Lager für ${produktlinieLabel(produktlinie)}`}
                                                                    style={{minWidth: 170}}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {(typ === "zahl" || typ === "text") && (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input
                                                            id={`cosmos-einstellung-wert-${e.id}`}
                                                            type={typ === "zahl" ? "number" : "text"}
                                                            className="form-control form-control-sm"
                                                            style={{maxWidth: 160}}
                                                            value={wert}
                                                            onChange={(ev) => setWert(ev.target.value)}
                                                        />
                                                        {typ === "zahl" && e.einheit && <span className="small text-muted">{e.einheit}</span>}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-grow-1" style={{minWidth: 220}}>
                                                <HeaderWithHelp help="Pflichtfeld: wird als neue Herkunft gespeichert, die Sicherheit springt danach auf „gepflegt“.">
                                                    <label className="form-label small mb-1" htmlFor={`cosmos-einstellung-begruendung-${e.id}`}>
                                                        Begründung (Pflichtfeld)
                                                    </label>
                                                </HeaderWithHelp>
                                                <input
                                                    id={`cosmos-einstellung-begruendung-${e.id}`}
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Warum wird der Wert geändert?"
                                                    value={begruendung}
                                                    onChange={(ev) => setBegruendung(ev.target.value)}
                                                />
                                            </div>
                                            <div className="d-flex gap-2 mb-1">
                                                <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-einstellung-abbrechen-${e.id}`}>Verwirft die Änderung, ohne zu speichern.</Tooltip>}>
                                                    <span>
                                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={abbrechen}>
                                                            Abbrechen
                                                        </button>
                                                    </span>
                                                </OverlayTrigger>
                                                <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-einstellung-speichern-${e.id}`}>Speichert den neuen Wert – wirkt erst nach der nächsten Berechnung.</Tooltip>}>
                                                    <span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary"
                                                            disabled={speichert || !begruendung.trim()}
                                                            onClick={() => speichern(e)}
                                                        >
                                                            Speichern
                                                        </button>
                                                    </span>
                                                </OverlayTrigger>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </Table>
    );
}
